import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Users, Plug } from "@phosphor-icons/react";
import { FEATURES, ALL_FEATURE_KEYS } from "@/lib/features";

const brl = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const empty = { name: "", price: "", max_attendants: "", max_connections: "", features: "", enabled_features: [...ALL_FEATURE_KEYS] };

export default function MasterPlans() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/plans").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty, enabled_features: [...ALL_FEATURE_KEYS] }); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, price: String(p.price),
      max_attendants: String(p.max_attendants),
      max_connections: String(p.max_connections),
      features: (p.features || []).join("\n"),
      enabled_features: p.enabled_features && p.enabled_features.length > 0 ? [...p.enabled_features] : [...ALL_FEATURE_KEYS],
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      name: form.name.trim(),
      price: parseFloat(form.price || 0),
      max_attendants: parseInt(form.max_attendants || 0),
      max_connections: parseInt(form.max_connections || 0),
      features: form.features.split("\n").map((x) => x.trim()).filter(Boolean),
      enabled_features: form.enabled_features,
    };
    try {
      if (editing) {
        await api.put(`/plans/${editing.id}`, payload);
        toast.success("Plano atualizado");
      } else {
        await api.post("/plans", payload);
        toast.success("Plano criado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir o plano "${p.name}"?`)) return;
    try {
      await api.delete(`/plans/${p.id}`);
      toast.success("Plano removido");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Master</p>
          <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1">Planos</h1>
          <p className="text-sm text-[#5C5C59] mt-2">Configure os limites que cada cliente terá.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNew}
              data-testid="new-plan-btn"
              className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal transition-colors duration-200"
            >
              <Plus size={18} weight="bold" className="mr-2" /> Novo plano
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white rounded-none border-[#E5E5E0] max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar plano" : "Novo plano"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Nome</Label>
                <Input data-testid="plan-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Preço (BRL)</Label>
                  <Input data-testid="plan-price-input" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Atendentes</Label>
                  <Input data-testid="plan-attendants-input" type="number" value={form.max_attendants} onChange={(e) => setForm({ ...form, max_attendants: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Conexões</Label>
                  <Input data-testid="plan-connections-input" type="number" value={form.max_connections} onChange={(e) => setForm({ ...form, max_connections: e.target.value })} className="rounded-none mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Recursos (1 por linha)</Label>
                <textarea
                  data-testid="plan-features-input"
                  value={form.features}
                  onChange={(e) => setForm({ ...form, features: e.target.value })}
                  className="w-full mt-1 border border-[#E5E5E0] p-2 text-sm min-h-[100px] font-mono"
                />
              </div>

              {/* Enabled features (permissions this plan unlocks) */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold">Funções liberadas neste plano</Label>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, enabled_features: form.enabled_features.length === FEATURES.length ? [] : [...ALL_FEATURE_KEYS] })}
                    className="text-[10px] font-bold uppercase tracking-wider text-[#0F4C3A] hover:underline"
                    data-testid="plan-enabled-toggle-all"
                  >
                    {form.enabled_features.length === FEATURES.length ? "Desmarcar todas" : "Marcar todas"}
                  </button>
                </div>
                <p className="text-[10px] text-[#5C5C59] mb-2">
                  O que os clientes deste plano poderão acessar. Se nada for marcado, o plano libera tudo.
                </p>
                <div className="grid grid-cols-2 gap-1 border border-[#E5E5E0] p-2" data-testid="plan-enabled-features">
                  {FEATURES.map((f) => (
                    <label key={f.key} className="flex items-center gap-2 px-2 py-1 text-xs cursor-pointer hover:bg-[#F7F7F5]">
                      <input
                        type="checkbox"
                        checked={form.enabled_features.includes(f.key)}
                        onChange={() => setForm((prev) => ({
                          ...prev,
                          enabled_features: prev.enabled_features.includes(f.key)
                            ? prev.enabled_features.filter((k) => k !== f.key)
                            : [...prev.enabled_features, f.key],
                        }))}
                        data-testid={`plan-feature-${f.key}`}
                        className="accent-[#0F4C3A]"
                      />
                      <span>{f.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[10px] text-[#0F4C3A] mt-1 font-semibold">
                  {form.enabled_features.length} de {FEATURES.length} liberadas
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-none">Cancelar</Button>
              <Button data-testid="plan-save-btn" onClick={save} className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plans-grid">
        {plans.map((p) => (
          <div key={p.id} className="bg-white border border-[#E5E5E0] p-5 shadow-brutal shadow-brutal-hover transition-shadow duration-200">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#5C5C59]">Plano</p>
                <h3 className="text-2xl font-black tracking-tight text-[#1A1A1A] mt-1">{p.name}</h3>
                <div className="mt-2 font-mono text-lg text-[#0F4C3A] font-bold">{brl(p.price)}<span className="text-xs text-[#5C5C59] font-normal">/mês</span></div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(p)} data-testid={`edit-plan-${p.id}`} className="p-2 hover:bg-[#F0F0EE] transition-colors duration-200"><PencilSimple size={16} /></button>
                <button onClick={() => remove(p)} data-testid={`delete-plan-${p.id}`} className="p-2 hover:bg-[#F0F0EE] text-[#DC2626] transition-colors duration-200"><Trash size={16} /></button>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-sm border-t border-[#E5E5E0] pt-4">
              <div className="flex items-center gap-2"><Users size={16} weight="bold" className="text-[#0F4C3A]" /><span className="font-semibold">{p.max_attendants}</span> atendentes</div>
              <div className="flex items-center gap-2"><Plug size={16} weight="bold" className="text-[#0F4C3A]" /><span className="font-semibold">{p.max_connections}</span> conexões</div>
            </div>
            {p.features?.length > 0 && (
              <ul className="mt-4 text-xs text-[#5C5C59] space-y-1">
                {p.features.map((f, i) => <li key={i}>· {f}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
